import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  Button,
  Grid,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  RocketLaunch as RocketLaunchIcon,
  Explore as ExploreIcon,
  Refresh as RefreshIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import AgentCard from './AgentCard';
import AgentListView from './AgentListView';

const AgentOverview = ({ 
  onLaunchAgent, 
  onExploreTemplates, 
  activeAgents = [], 
  onRefresh, 
  onDelete, 
  isLoading 
}) => {
  const theme = useTheme();
  const [viewMode, setViewMode] = useState('cards');

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  const renderCreateAgentSection = () => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
      <Card 
        sx={{ 
          flex: '1 1 50%', 
          textAlign: 'center', 
          py: 6,
          mr: 2,
          background: '#800080',
        }}
      >
        <Typography variant="h5" sx={{ color: 'white' }}>
          Explore Pre-built AI Agents
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: 'white' }}>
          Leverage pre-configured AI solutions that cover most business needs, eliminating the hassle of hardware setup.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={onExploreTemplates}
          startIcon={<ExploreIcon />}
          sx={{ 
            px: 6, 
            py: 2,
            fontSize: '1.2rem',
            background: 'white',
            color: '#800080',
            '&:hover': {
              background: 'white',
              transform: 'translateY(-2px)'
            },
            transition: 'transform 0.2s'
          }}
        >
          Discover AI Agents
        </Button>
      </Card>
      
      <Card 
        sx={{ 
          flex: '1 1 50%', 
          textAlign: 'center', 
          py: 6,
          ml: 2,
          background: theme.palette.primary.main,
        }}
      >
        <Typography variant="h5" sx={{ color: 'white' }}>
          Build tailored AI Agents
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: 'white' }}>
          Tailor your AI Agent to meet specific business requirements with precision.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={onLaunchAgent}
          startIcon={<RocketLaunchIcon />}
          sx={{ 
            px: 6, 
            py: 2,
            fontSize: '1.2rem',
            background: 'white',
            color: 'primary.main',
            '&:hover': {
              background: 'white',
              transform: 'translateY(-2px)'
            },
            transition: 'transform 0.2s'
          }}
        >
          Build AI Agent
        </Button>
      </Card>
    </Box>
  );

  const renderAgentListHeader = () => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
      <Typography variant="h6">Your Agents ({activeAgents.length})</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              color: 'rgba(255, 255, 255, 0.7)',
              borderColor: 'rgba(255, 255, 255, 0.2)',
              '&.Mui-selected': {
                backgroundColor: theme.palette.primary.main,
                color: 'black',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                }
              },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              }
            }
          }}
        >
          <ToggleButton value="cards" aria-label="card view">
            <ViewModuleIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="list" aria-label="list view">
            <ViewListIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
        <Button
          startIcon={<RefreshIcon />}
          onClick={onRefresh}
          disabled={isLoading}
        >
          {isLoading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Box>
    </Box>
  );

  const renderAgentsList = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (activeAgents.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 6, backgroundColor: 'background.paper', borderRadius: 2 }}>
          <Typography variant="h6" color="text.secondary">
            No agents found. Create your first agent to get started.
          </Typography>
        </Box>
      );
    }

    if (viewMode === 'cards') {
      return (
        <Grid container spacing={2}>
          {activeAgents.map((agent) => (
            <Grid item xs={12} sm={6} md={4} key={agent.id}>
              <AgentCard 
                agent={agent} 
                onDelete={onDelete}
              />
            </Grid>
          ))}
        </Grid>
      );
    }

    return (
      <AgentListView 
        agents={activeAgents}
        onDelete={onDelete}
      />
    );
  };

  return (
    <Box>
      {renderCreateAgentSection()}
      {renderAgentListHeader()}
      {renderAgentsList()}
    </Box>
  );
};

AgentOverview.propTypes = {
  onLaunchAgent: PropTypes.func.isRequired,
  onExploreTemplates: PropTypes.func.isRequired,
  activeAgents: PropTypes.array,
  onRefresh: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isLoading: PropTypes.bool
};

export default AgentOverview; 